import type {
  AstraSyncConfig,
  RegisterOptions,
  RegistrationResponse,
  VerifyResponse,
  HealthResponse,
  ApiErrorResponse,
} from './types';
import { AstraSyncError, KYDRequiredError, AuthenticationError } from './errors';

const DEFAULT_BASE_URL = 'https://api.kya.astrasync.ai';

/**
 * AstraSync SDK client for registering and managing AI agents.
 *
 * @example
 * ```typescript
 * const client = new AstraSync({ apiKey: 'kya_your_api_key' });
 * const result = await client.register({
 *   name: 'My Agent',
 *   model: { modelName: 'gpt-4o', modelProvider: 'openai', modelType: 'llm' },
 * });
 * ```
 */
export class AstraSync {
  private readonly baseUrl: string;
  private readonly apiKey?: string;
  private readonly email?: string;
  private readonly password?: string;
  private readonly privateKey?: string;
  private cachedJwt?: string;
  private jwtExpiresAt?: number;

  constructor(config: AstraSyncConfig = {}) {
    this.baseUrl = (
      config.baseUrl ||
      process.env.ASTRASYNC_API_URL ||
      DEFAULT_BASE_URL
    ).replace(/\/+$/, '');

    this.apiKey = config.apiKey || process.env.ASTRASYNC_API_KEY;
    this.email = config.email;
    this.password = config.password;
    this.privateKey = config.privateKey;

    if (!this.apiKey && !this.email) {
      throw new AuthenticationError(
        'Authentication required. Provide apiKey, or email+password. ' +
        'Set ASTRASYNC_API_KEY env var or pass config to constructor.',
      );
    }

    if (this.email && !this.password) {
      throw new AuthenticationError('Password is required when using email authentication.');
    }
  }

  /**
   * Register a new AI agent on the AstraSync KYA Platform.
   * Sends full payload including model, framework, PDLSS, and metadata.
   */
  async register(options: RegisterOptions): Promise<RegistrationResponse> {
    const body: Record<string, unknown> = {
      name: options.name,
      ...(options.description && { description: options.description }),
      ...(options.agentType && { agentType: options.agentType }),
      ...(options.apiEndpoint && { apiEndpoint: options.apiEndpoint }),
      ...(options.model && { model: options.model }),
      ...(options.framework && { framework: options.framework }),
      ...(options.metadata && { metadata: options.metadata }),
      ...(options.pdlss && { pdlss: options.pdlss }),
    };

    return this.request<RegistrationResponse>('POST', '/api/agents/register', body);
  }

  /**
   * Look up an agent's public profile by ASTRA ID or UUID.
   */
  async verify(agentId: string): Promise<VerifyResponse> {
    return this.request<VerifyResponse>('GET', `/api/agents/verify/${agentId}`);
  }

  /**
   * Check API health.
   */
  async health(): Promise<HealthResponse> {
    const res = await fetch(`${this.baseUrl}/api/health/`);
    if (!res.ok) {
      throw new AstraSyncError(`Health check failed: ${res.status}`, res.status);
    }
    return res.json() as Promise<HealthResponse>;
  }

  // ── Private helpers ──────────────────────────────────────────────

  private async request<T>(method: string, endpoint: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Set auth header
    const token = await this.getAuthToken();
    headers['Authorization'] = `Bearer ${token}`;

    // Sign request if private key is configured
    if (this.privateKey) {
      const signature = await this.signRequest(method, endpoint, body || {});
      headers['X-AstraSync-Signature'] = signature;
    }

    const res = await fetch(url, {
      method,
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({ error: res.statusText })) as ApiErrorResponse;

      // Handle KYD_REQUIRED specifically
      if (res.status === 403 && errorBody.code === 'KYD_REQUIRED') {
        throw new KYDRequiredError(errorBody);
      }

      throw new AstraSyncError(
        errorBody.error || `Request failed: ${res.status}`,
        res.status,
        errorBody.code,
      );
    }

    return res.json() as Promise<T>;
  }

  private async getAuthToken(): Promise<string> {
    // API key auth — use directly
    if (this.apiKey) {
      return this.apiKey;
    }

    // Email+password — login and cache JWT
    if (this.cachedJwt && this.jwtExpiresAt && Date.now() < this.jwtExpiresAt) {
      return this.cachedJwt;
    }

    const res = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: this.email, password: this.password }),
    });

    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({})) as Record<string, unknown>;
      throw new AuthenticationError(
        (errorBody.message as string) || (errorBody.error as string) || 'Login failed',
      );
    }

    const data = await res.json() as { data: { token: string } };
    this.cachedJwt = data.data.token;
    // Cache for 6 days (tokens expire in 7)
    this.jwtExpiresAt = Date.now() + 6 * 24 * 60 * 60 * 1000;

    return this.cachedJwt;
  }

  /**
   * Sign a request using secp256k1 (ethers.js).
   * Canonical message format: METHOD:ENDPOINT:SORTED_JSON_BODY
   * Must match apps/backend/src/services/signature-verify.service.ts exactly.
   */
  private async signRequest(method: string, endpoint: string, body: unknown): Promise<string> {
    const { Wallet } = await import('ethers');
    const sorted = this.sortObjectKeys(body);
    const canonical = `${method}:${endpoint}:${JSON.stringify(sorted)}`;
    const wallet = new Wallet(this.privateKey!);
    return wallet.signMessage(canonical);
  }

  /** Recursively sort object keys for canonical JSON representation. */
  private sortObjectKeys(obj: unknown): unknown {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(item => this.sortObjectKeys(item));
    }
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      sorted[key] = this.sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }
}
